import React, {
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { deleteReportForSavedSelectionById } from '@/endpoints/itasca/reports/deleteReportForSavedSelectionById';
import { useAuth } from '@/providers/auth-provider';

import { addReportToSavedSelection } from '../../../endpoints/itasca/land/saved-selections/addReportToSavedSelection';
import { deleteSavedSelectionCSV } from '../../../endpoints/itasca/land/saved-selections/deleteSavedSelectionCSV';
import { removeReportFromSavedSelection } from '../../../endpoints/itasca/land/saved-selections/removeReportFromSavedSelection';
import { RootState } from '../../../redux/reducers';
import { clearFileUploadModal } from '../../../redux/slices/fileUploadModalSlice';
import Logger from '../../../utils/logger';
import FileUploadModal, {
  FileUploadType,
} from '../../common/file-upload-modal';

export const NavigationScrollingDivId = 'navigation-scrolling-div';

// For the file modal
const acceptedFileString =
  '.png, .jpg, .pdf, .doc, .docx, .xlsx, .xls, .csv, .webp';
const acceptedFileType = {
  'application/msword': ['.doc'],
  'application/pdf': ['.pdf'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
    '.xlsx',
  ],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    '.docx',
  ],
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
  'text/csv': ['.csv'],
};

interface Props {
  setShouldRefreshSelectionId?: React.Dispatch<
    React.SetStateAction<number | null>
  >;
}

const SavedSelectionFileUploadModal: FunctionComponent<Props> = ({
  setShouldRefreshSelectionId: setShouldRefresh = undefined,
}) => {
  const { hasActiveSession } = useAuth();
  const { isFileUploadModalOpen, title, files, isFetchingFiles } = useSelector(
    (s: RootState) => s.fileUploadModal
  );

  const { savedClaimLand } = useSelector((s: RootState) => s.savedSelection);
  const dispatch = useDispatch();
  const [shouldRefreshFilesSelectionId, setShouldRefreshFilesSelectionId] =
    useState<number | null>(null);

  useEffect(() => {
    setShouldRefresh?.(shouldRefreshFilesSelectionId);

    if (shouldRefreshFilesSelectionId) {
      setShouldRefreshFilesSelectionId(null);
    }
  }, [setShouldRefresh, shouldRefreshFilesSelectionId]);

  /**
   * Adds the uploaded file to the DB
   * @param acceptedFile
   * @returns an object with the id if successful, null if failed
   */
  const onFilesUpload = useCallback(
    async (acceptedFile: File) => {
      try {
        if (!savedClaimLand || !hasActiveSession) return null;

        const fd = new FormData();

        fd.append('datafile', acceptedFile);
        fd.append('selection', String(savedClaimLand.id));
        fd.append('name', acceptedFile.name);

        const { res } = await addReportToSavedSelection(savedClaimLand.id, fd);

        if (res) {
          setShouldRefreshFilesSelectionId(savedClaimLand.id);

          return { id: res.data.id };
        }
      } catch (err) {
        Logger.error(err);
      }

      return null;
    },
    [savedClaimLand, hasActiveSession]
  );

  /**
   * Deletes file from DB
   * @param file
   * @returns void
   */
  const onFileDelete = useCallback(
    async (fileId: string | number) => {
      if (!hasActiveSession || !savedClaimLand) return;

      try {
        await removeReportFromSavedSelection(savedClaimLand.id, fileId);
      } catch (err) {
        Logger.error(err);
      } finally {
        setShouldRefreshFilesSelectionId(savedClaimLand?.id);
      }
    },
    [savedClaimLand, hasActiveSession]
  );

  /**
   * Deletes generated csv file from DB
   * @param file
   * @returns void
   */
  const onGeneratedCSVDelete = useCallback(
    async (fileId: string | number) => {
      if (!hasActiveSession || !savedClaimLand) return;

      try {
        await deleteSavedSelectionCSV(savedClaimLand.id, fileId);
      } catch (err) {
        Logger.error(err);
      }
    },
    [savedClaimLand, hasActiveSession]
  );

  const onDeleteGeneratedPDF = useCallback(
    async (reportId: string | number): Promise<void> => {
      if (!hasActiveSession || !savedClaimLand) return;

      try {
        await deleteReportForSavedSelectionById({
          reportId,
          savedSelectionId: savedClaimLand.id,
        });
      } catch (err) {
        Logger.error(err);
      }
    },
    [hasActiveSession, savedClaimLand]
  );
  const handleOnClose = useCallback(() => {
    dispatch(clearFileUploadModal());
  }, [dispatch]);

  return (
    <FileUploadModal
      acceptedFileString={acceptedFileString}
      acceptedFileType={acceptedFileType}
      files={files}
      handleOnClose={handleOnClose}
      isFetchingFiles={isFetchingFiles}
      isOpen={isFileUploadModalOpen}
      savedSelectionId={savedClaimLand?.id}
      title={title}
      type={FileUploadType.VIEW_UPLOAD}
      onDelete={onFileDelete}
      onDeleteGeneratedCSV={onGeneratedCSVDelete}
      onDeleteGeneratedPDF={onDeleteGeneratedPDF}
      onUpload={onFilesUpload}
    />
  );
};

export default memo(SavedSelectionFileUploadModal);
